<app-modal-v2 #bulkUpdateModal [customClassName]="'bulk-update-modal'">
  <div class="modal-content">
    <div class="modal-header">
      <h2 class="modal-title text-center">Update Cards</h2>
    </div>

    <div class="modal-body">
      <!-- Result panel shown after a successful submit -->
      <div *ngIf="result" class="bulk-update-result">
        <p class="result-message">
          Successfully updated <strong>{{ result.updatedCount }}</strong>
          card{{ result.updatedCount !== 1 ? 's' : '' }} in range{{ result.rangesLabel.includes(',') ? 's' : '' }}
          <strong>{{ result.rangesLabel }}</strong>.
        </p>
        <p *ngIf="result.missingNumbers.length > 0" class="result-missing">
          {{ result.missingNumbers.length }} card{{ result.missingNumbers.length !== 1 ? 's' : '' }} not found:
          <strong>{{ result.missingNumbers.join(', ') }}</strong>.
        </p>
      </div>

      <!-- Main form, hidden once a result is shown -->
      <ng-container *ngIf="!result">
        <!-- Ranges section -->
        <form [formGroup]="rangesForm">
          <div class="ranges-header">
            <span class="ranges-title">Card number ranges</span>
            <button type="button" class="btn btn-primary btn-circle-add" (click)="addRange()" title="Add range">+</button>
          </div>
          <div formArrayName="ranges">
            <ng-container *ngFor="let rangeRow of ranges.controls; let i = index" [formGroupName]="i">
              <div class="range-row">
                <input type="number" class="form-control range-input" formControlName="fromCardNumber" min="1" placeholder="From">
                <input type="number" class="form-control range-input" formControlName="toCardNumber" min="1" placeholder="To">
                <button type="button" class="btn btn-outline btn-delete btn-circle-remove" (click)="removeRange(i)" *ngIf="ranges.length > 1">+</button>
                <div *ngIf="ranges.length <= 1" class="range-remove-spacer"></div>
              </div>
              <div *ngIf="rangeRow.errors?.['fromAfterTo']" class="text-danger range-error">
                "From" must be less than or equal to "To".
              </div>
            </ng-container>
          </div>
          <div *ngIf="rangesForm.errors?.['maxRangeExceeded']" class="text-danger range-error">
            Total card numbers across all ranges cannot exceed {{ maxRangeSize }} in a single update. Please split into smaller batches.
          </div>
        </form>

        <!-- Update options -->
        <form [formGroup]="updateForm">
          <div class="update-options">

            <!-- Assign Card -->
            <div class="update-option">
              <div class="update-option-header">
                <label class="checkbox mb-0">
                  <input type="checkbox" formControlName="applyAssign">
                  <span></span>
                </label>
                <span class="update-option-label">Assign Card</span>
              </div>
              <div *ngIf="updateForm.get('applyAssign').value" class="update-option-body">
                <div class="form-group">
                  <label>Trial<span class="required-star">*</span></label>
                  <app-trial-autocomplete #trialAutocomplete (valueChanged)="onAssignCardTrialFilterChanged($event)"
                                          [form]="updateForm" controlName="trialId"
                                          placeholder="Select a trial..."></app-trial-autocomplete>
                </div>
                <div class="form-group" [hidden]="updateForm.get('sendToPatient').value">
                  <label>Site</label>
                  <app-dropdown-input #siteAutocomplete [options]="assignCardsSiteOptions" [canClear]="true"
                                      [parentForm]="updateForm" [name]="'siteId'"></app-dropdown-input>
                </div>
                <div class="form-group" [hidden]="updateForm.get('sendToPatient').value">
                  <label>Site contact</label>
                  <textarea formControlName="siteContact"></textarea>
                </div>
                <div class="form-group" [hidden]="!updateForm.get('sendToPatient').value">
                  <label>Patient details</label>
                  <input type="text" class="form-control" formControlName="patientDetails">
                </div>
                <div class="form-group checkbox-row">
                  <label class="checkbox mb-0">
                    <input formControlName="sendToPatient" id="bulk-send-to-patient" type="checkbox" value="true">
                    <span></span>
                  </label>
                  <label class="checkbox-label mb-0" for="bulk-send-to-patient">Send to patient?</label>
                </div>
              </div>
            </div>

            <!-- Mark as (mutually exclusive delivery options) -->
            <div class="update-option">
              <div class="update-option-header">
                <span class="update-option-label">Mark as</span>
                <a *ngIf="updateForm.get('deliveryAction').value" class="clear-link" (click)="clearDeliveryAction()">Clear</a>
              </div>
              <div class="delivery-options">
                <button type="button" class="delivery-option" [class.is-active]="updateForm.get('deliveryAction').value === 'received'" (click)="setDeliveryAction('received')">Received</button>
                <button type="button" class="delivery-option" [class.is-active]="updateForm.get('deliveryAction').value === 'sent'" (click)="setDeliveryAction('sent')">Sent</button>
                <button type="button" class="delivery-option" [class.is-active]="updateForm.get('deliveryAction').value === 'missing'" (click)="setDeliveryAction('missing')">Missing</button>
              </div>
              <div *ngIf="updateForm.get('deliveryAction').value === 'sent'" class="sent-expansion">
                <div class="form-group">
                  <label>Courier Cost</label>
                  <textarea formControlName="courierCost"></textarea>
                </div>
                <div class="form-group">
                  <label>Tracking No.</label>
                  <textarea formControlName="trackingNumber"></textarea>
                </div>
              </div>
            </div>

            <!-- Set Expiry Date -->
            <div class="update-option">
              <div class="update-option-header">
                <label class="checkbox mb-0">
                  <input type="checkbox" formControlName="applyExpiry">
                  <span></span>
                </label>
                <span class="update-option-label">Set Expiry Date</span>
              </div>
              <div *ngIf="updateForm.get('applyExpiry').value" class="update-option-body">
                <div class="form-group">
                  <div class="datepicker-v2-input">
                    <mat-form-field appearance="fill">
                      <input matInput [matDatepicker]="expiryDp" formControlName="expiryDate" placeholder="MM/YYYY">
                      <mat-datepicker-toggle matIconSuffix [for]="expiryDp"></mat-datepicker-toggle>
                      <mat-datepicker #expiryDp startView="multi-year" (monthSelected)="setExpiryMonthAndYear($event, expiryDp)">
                      </mat-datepicker>
                    </mat-form-field>
                  </div>
                </div>
              </div>
            </div>

            <!-- Set Office -->
            <div class="update-option">
              <div class="update-option-header">
                <label class="checkbox mb-0">
                  <input type="checkbox" formControlName="applyOffice">
                  <span></span>
                </label>
                <span class="update-option-label">Set Office</span>
              </div>
              <div *ngIf="updateForm.get('applyOffice').value" class="update-option-body">
                <div class="form-group">
                  <div class="office-dropdown-control">
                    <app-dropdown-input #officeDropdown
                                        [options]="officeOptions"
                                        [parentForm]="updateForm"
                                        name="office"
                                        placeholder="Select office...">
                    </app-dropdown-input>
                    <a class="clear" (click)="clearOffice()" *ngIf="updateForm.get('office').value != null">
                      <i class="fas fa-times" aria-hidden="true"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </form>
      </ng-container>
    </div>

    <div class="modal-footer">
      <div class="row w-100 m-0" *ngIf="result">
        <div class="col-12 pr-0">
          <button (click)="closeResult()" class="btn btn-primary btn-full">Close</button>
        </div>
      </div>
      <div class="row w-100 m-0" *ngIf="!result">
        <div class="col-6 pl-0">
          <a (click)="bulkUpdateModal.hide()" class="btn btn-secondary btn-full">Cancel</a>
        </div>
        <div class="col-6 pr-0">
          <button (click)="onSubmit()" class="btn btn-primary btn-full" [disabled]="isSubmitDisabled">
            <span *ngIf="updateForm.get('isBusy').value">
              <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            </span>
            <span *ngIf="!updateForm.get('isBusy').value">Apply Updates</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</app-modal-v2>
